/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM, T } from "@adapptio/lib-components";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Transition } from "react-transition-group";
import { TOAST_TYPE } from "../../../toasts/IToast";
import { IToastEntry, TOAST_ENTRY_STATE } from "../../../toasts/ToastService";
import { Icon, ICON_SIZE } from "../Icon";
import { Spinner, SPINNER_SIZE } from "../Spinner";

export interface IToastItemProps {
	entry: IToastEntry;
	onClose: (toastId: string) => void;
	onMouseEnter: (toastId: string) => void;
	onMouseLeave: (toastId: string) => void;
}

const TRANSITION_TIMEOUT = 600;

export const ToastItem: FC<IToastItemProps> = ({ entry, onClose, onMouseEnter, onMouseLeave }) => {
	const itemElRef = useRef<HTMLDivElement>();

	const [ state, setState ] = useState(entry.state);
	const [ config, setConfig ] = useState(entry.config);

	useEffect(() => {
		const onChange = () => {
			setState(entry.state);
			setConfig(entry.config);
		};

		onEvent(entry.onChange, onChange);

		return () => {
			offEvent(entry.onChange, onChange);
		};
	}, [ entry ]);

	useEffect(() => {
		if (!itemElRef.current) {
			return;
		}

		if (state === TOAST_ENTRY_STATE.READY) {
			// itemElRef.current.style.width = itemElRef.current.scrollWidth + "px";
			itemElRef.current.style.height = itemElRef.current.scrollHeight + "px";
		} else {
			itemElRef.current.style.height = "0px";
		}
	}, [ state, config, itemElRef.current ]);

	const handleClose = useCallback(() => {
		if (config.isClosable !== false) {
			onClose(entry.id);
		}
	}, [ config, onClose ]);

	const handleMouseEnter = useCallback(() => {
		onMouseEnter(entry.id);
	}, [ onMouseEnter ]);

	const handleMouseLeave = useCallback(() => {
		onMouseLeave(entry.id);
	}, [ onMouseLeave ]);

	let typeIcon = null;

	switch (config.type) {
		case TOAST_TYPE.SUCCESS: {
			typeIcon = "mdi/check";
			break;
		}
		case TOAST_TYPE.WARNING: {
			typeIcon = "mdi/alert";
			break;
		}
		case TOAST_TYPE.ERROR: {
			typeIcon = "mdi/alert-circle";
			break;
		}
		default: {
			typeIcon = "mdi/information";
		}
	}

	const icon = config.icon || typeIcon;

	const baseClass = BEM("edt-toasts", "item", {
		"is-ready": state === TOAST_ENTRY_STATE.READY,
		"with-details": config.details ? true : false,
		"is-closable": config.isClosable !== false,
		type: config.type
	});

	baseClass.add("edt-base");

	return (
		<div
			className={baseClass.toClassName()}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			ref={itemElRef}
		>
			<div className={baseClass.element("item-inner").toClassName()}>
				<div
					className={baseClass
						.element("item-icon", { "is-loading": config.type === TOAST_TYPE.LOADING })
						.toClassName()}
				>
					<Icon name={icon} size={ICON_SIZE.SMALL} />
					<Transition
						in={config.type === TOAST_TYPE.LOADING}
						timeout={TRANSITION_TIMEOUT}
						unmountOnExit
					>
						{(transitionState) => (
							<Spinner
								size={SPINNER_SIZE.SMALL}
								className={`edt-spinner--${transitionState}`}
							/>
						)}
					</Transition>
				</div>
				<div className={baseClass.element("item-content").toClassName()}>
					<div className={baseClass.element("item-message").toClassName()}>
						<T domain="editor" term={config.message} />
					</div>
					{config.details ? (
						<div className={baseClass.element("item-details").toClassName()}>
							<T domain="editor" term={config.details} />
						</div>
					) : null}
					{Array.isArray(config.actions) && config.actions.length > 0 ? (
						<div className={baseClass.element("item-actions").toClassName()}>
							{config.actions.map((action) => {
								return (
									<button
										className={baseClass.element("item-action-btn").toClassName()}
										title={action.title}
										onClick={action.onClick}
									>
										{action.label}
									</button>
								);
							})}
						</div>
					) : null}
					<button
						className={baseClass.element("item-close-btn").toClassName()}
						onClick={handleClose}
					>
						<Icon name="mdi/close" size={ICON_SIZE.XSMALL} />
					</button>
				</div>
			</div>
		</div>
	);
};
